import { PublicClientApplication, type Configuration, LogLevel } from '@azure/msal-browser'
import { env } from '@/config/env'

/**
 * authConfig.ts
 * Single source of truth for MSAL configuration.
 * all MSAL setup lives in src/features/auth/authConfig.ts.
 *
 * redirectUri uses BASE_URL so it works under any hosted path (e.g. /digitalbadging/).
 */

const baseUrl = import.meta.env.BASE_URL.replace(/\/$/, '')
const redirectUri = `${window.location.origin}${baseUrl}/`

const msalConfiguration: Configuration = {
  auth: {
    clientId:              env.VITE_AZURE_AD_CLIENT_ID,
    authority:             env.VITE_AZURE_AD_AUTHORITY,
    redirectUri,
    postLogoutRedirectUri: redirectUri,
  },
  cache: {
    cacheLocation: 'sessionStorage', // tokens NOT shared across browser tabs
  },
  system: {
    loggerOptions: {
      loggerCallback: (level, message, containsPii) => {
        if (containsPii) return
        if (import.meta.env.DEV) {
          // eslint-disable-next-line no-console
          console.log(`[MSAL][${LogLevel[level]}] ${message}`)
        }
      },
      logLevel: import.meta.env.DEV ? LogLevel.Info : LogLevel.Error,
    },
  },
}

/** PublicClientApplication singleton — passed to <MsalProvider> in App.tsx */
export const msalInstance = new PublicClientApplication(msalConfiguration)

/**
 * API scope — sourced from VITE_API_SCOPE (matches Blazor AzureAdTokenConfig.Scopes).
 * Example: 'https://deventregistrations.deloitte.com/digitalbadging/user_impersonation'
 * Falls back to VITE_MSAL_CLIENT_ID/.default if VITE_API_SCOPE is not set.
 */
const resolvedApiScope: string =
  env.VITE_API_SCOPE ?? `${env.VITE_MSAL_CLIENT_ID ?? ''}/.default`

/** Scopes for interactive login (openid + profile + API scope) */
export const loginRequest = {
  scopes: ['openid', 'profile', 'email', resolvedApiScope],
}

/** Scopes for silent token acquisition for API calls */
export const apiTokenRequest = {
  scopes: [resolvedApiScope],
}

/** Convenience array — used by services that need the raw scope string */
export const apiScopes: string[] = apiTokenRequest.scopes

